/**
 * Générateur — Représenter la dépendance de deux grandeurs par un graphique.
 * Attendu ID : 176 | Niveau : 4ème | Domaine : Fonctions (O13)
 *
 * Réécrit le 2026-09-25 (vague « sept objectifs 4e fermés »), décisions du
 * coordinateur (DECISIONS_176, épreuve de Musk) appliquées telles quelles.
 *
 * PLAFOND (Eduscol, attendus de fin de 4e, « Comprendre et utiliser la notion
 * de fonction ») : l. 285 « Il représente la dépendance de deux grandeurs par
 * un graphique. » ; exemple de réussite l. 301-302 « Il sait construire la
 * représentation graphique de l'aire blanche en fonction de la longueur du
 * côté des carrés. » (le rectangle 20 cm × 13 cm privé de quatre carrés
 * superposables, dont 175 produit la formule 260 − 4x²). Rien d'autre : ni la
 * formule (175, l. 284), ni la lecture sur le graphique (177, l. 286-288), ni
 * « proportionnel ou pas » (5e l. 1351, 4e 171).
 *
 * CE QUI EST SORTI (les six formats de juillet 2026, cinq sans aucun graphique) :
 *   - « Tableau à trous » (graphique_tableau_texte_trou_saisie) : substitution
 *     dans une formule donnée (5e l. 1349, 4e 1111), aucun repère.
 *   - « Placer les points » (graphique_placer_2d) : REMPLACÉ par la carte 1 ;
 *     il comparait deux tarifs (points gris du tarif proportionnel = 5e / 993),
 *     ignorait l'exemple du plafond (l'aire, non linéaire), 24 signatures,
 *     clé et onglet hors convention.
 *   - « QCM constat » (graphique_constat_qcm) : aucune figure, clé = l'option
 *     la plus longue à 100 %, propriété de 5e (alignés avec l'origine, 171).
 *   - « Seuil d'avantage » (graphique_seuil_texte) : équation (158-163) ou
 *     lecture d'un croisement (177) ; aucune ligne du plafond.
 *   - « Lecture » (graphique_lecture_texte) : aucun graphique malgré son nom,
 *     substitution (5e).
 *   - « Vrai/Faux » (graphique_avantage_vf) : trois lignes sur cinq à vérité
 *     constante, proportionnalité (171).
 *   - La situation « deux tarifs » (tarif simple w × n en gris) : SUPPRIMÉE,
 *     un seul tarif par carte ; cinéma et escalade (prix invraisemblables)
 *     ne sont pas repris.
 *
 * CE QUI RESTE : DEUX cartes, deux supports tirés à parts égales par
 * contenu.sousType.
 *   Support `aire` (le plafond, obligatoire) : le rectangle L × l privé de
 *   quatre carrés de côté x (figure de 175 : svgAireRestante de fonctions.js,
 *   ni cote ni lettre) ; A = L × l − 4x² ; L de 8 à 12, l de ⌈L/2⌉ à L − 1,
 *   L × l multiple de 4 et ≤ 100, l ≥ 5 (trois abscisses au moins) : les dix
 *   couples 8 × 5, 8 × 6, 8 × 7, 9 × 8, 10 × 6, 10 × 8, 11 × 8, 12 × 6, 12 × 7,
 *   12 × 8 (calculés par les règles, la liste décidée est vérifiée par
 *   l'auto-test) ; x entier de 0 à ⌊(l − 1)/2⌋ (les carrés ne se touchent
 *   pas) ; toute aire est un multiple de 4 → yStep 4. La formule est écrite
 *   avec la VALEUR du produit (« A = 40 − 4x² »), la forme de la clé de 175 et
 *   du plafond (« 260 − 4x² »).
 *   Support `tarif` (le cours 19Eq4e, la carte d'abonnement) : un abonnement
 *   à F €, puis 2 € l'entrée ; P = F + 2n ; F ∈ {12 ; 16 ; 24 ; 32} ; n de 0 à
 *   8 ; yStep 2 ; contextes piscine, patinoire, parc aquatique, tennis (les
 *   CONTEXTES de fonctions.js).
 *   Sacrifiés, jamais tirés : (20 ; 13) (SACRIFIE_AIRE de gen_175, hors plage
 *   ici) et (F ; v) = (20 ; 2) (SACRIFIE_TARIF de fonctions.js).
 *
 *   Carte 1 « Placer les points du graphique » (points_placer_points_2d,
 *   onglet Repère) : la situation, la formule en KaTeX, « Place le point M
 *   d'abscisse x = 1, le point N d'abscisse x = 2 et le point R d'abscisse
 *   x = 3. » : trois abscisses distinctes, x = 0 dans un tirage sur trois au
 *   plus (tiré d'abord, puis un couple qui offre trois abscisses non nulles
 *   quand x = 0 n'est pas tiré) ; le tableau de valeurs N'EST PAS donné (le
 *   cran de 4e : formule → points, à une échelle non unitaire). Repère donné,
 *   premier quadrant, étiquettes d'axes SANS lettre (« côté du carré (en
 *   cm) » / « aire blanche (en cm²) » ; « nombre d'entrées » / « prix (en €) »),
 *   xStep 1, yStep 4 (aire) ou 2 (tarif), yMax = le multiple de 20 strictement
 *   supérieur à L × l (≤ 100) ou le multiple de 10 strictement supérieur à
 *   F + 16 (≤ 50) : le point le plus haut n'est jamais sur le bord ;
 *   masquerCoordonnees (on lit l'échelle, pas le curseur) ; pas de point gris,
 *   pas de relier. Rappel brouillon (les trois substitutions se posent), pas
 *   de calculatrice. Les points sont nommés par pickLettresSains (trois
 *   lettres, l'ordre alphabétique = l'ordre des abscisses).
 *   Carte 2 « Quel graphique ? » (graphique_qcm_unique, onglet QCM : le QCM de
 *   l'attendu, jouable aux cartes) : même situation, même formule, « Quel
 *   graphique représente l'aire blanche en fonction du côté du carré ? » ;
 *   quatre panneaux SVG (imageData par choix, FORMATS §1 et §12), même
 *   repère, croix sur les points ET trait fin ; aire : clé (courbe
 *   décroissante depuis (0 ; L × l)), carre_oublie (droite L × l − 4x),
 *   aire_enlevee (courbe 4x² par l'origine), echange_xy (étiquettes d'axes
 *   permutées, dessin miroir) ; tarif : clé (droite depuis (0 ; F)),
 *   par_l_origine (2n), coefficients_echanges (2 + F × n, la droite raide qui
 *   sort par le haut du cadre), echange_xy. Aucun trait propre à la seule clé
 *   (la clé n'est ni la seule décroissante, ni la seule courbe, ni la seule à
 *   couper l'axe des ordonnées hors de l'origine : mesuré par l'auto-test sur
 *   2 000 tirages). Pas de rappel brouillon (rien ne se calcule), pas de
 *   calculatrice. Chaque panneau porte data-points="x:y;…" (les points
 *   tracés, dans les coordonnées du panneau) et data-axes pour le solveur.
 *
 * Second chemin (throw sinon) : chaque ordonnée recalculée autrement (aire
 * par morceaux (L − 2x) × l + 2x(l − 2x) ; tarif par la somme F + 2 + 2 + …),
 * chaque point sur la grille et dans la fenêtre, les quatre panneaux deux à
 * deux distincts, un seul panneau dont tous les points sont sur la formule.
 * Solveur test-side : tests/solvers/fonctions-4e.js (recalcul depuis le texte
 * rendu, lecture des data-points). Contrat des distracteurs exporté :
 * REGLES_DISTRACTEURS_QCM, codes dans _meta.params.distracteurs.
 * Deux cartes, deux formats, dont un QCM.
 */

import {
    randInt, randomChoice, randomChoiceMultiple,
    basePayload, makeQcm, tex, pickLettresSains,
} from '../../../utils';
import {
    CONTEXTES, auLieu, deMot, imgElement, svgAireRestante, ALT_AIRE_RESTANTE, SACRIFIE_TARIF,
} from './fonctions';
import { SACRIFIE_AIRE } from './gen_175';

const ATTENDU_ID = 176;
const L_POINTS = 'Placer les points du graphique';
const L_GRAPHIQUE = 'Quel graphique ?';
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

// ═════════ Support « aire » : le plafond ═════════

/** Le plus grand côté d'un carré : les carrés ne se touchent pas (2x < l). */
const xMaxAire = (l) => Math.floor((l - 1) / 2);

/**
 * Les couples (L ; l) du support aire, par les règles décidées : L de 8 à 12,
 * l de ⌈L/2⌉ à L − 1, L × l multiple de 4 et ≤ 100, trois abscisses au moins
 * (l ≥ 5), le couple d'Eduscol exclu. Dix couples (liste vérifiée par l'auto-test).
 */
export const couplesAire = () => {
    const out = [];
    for (let L = 8; L <= 12; L++) {
        for (let l = Math.ceil(L / 2); l <= L - 1; l++) {
            if (xMaxAire(l) < 2) continue;
            if ((L * l) % 4 !== 0 || L * l > 100) continue;
            if (L === SACRIFIE_AIRE.L && l === SACRIFIE_AIRE.l) continue;
            out.push({ L, l });
        }
    }
    return out;
};
const COUPLES_AIRE = couplesAire();

/** Le multiple de `pas` strictement supérieur à v. */
const multipleAuDessus = (v, pas) => (Math.floor(v / pas) + 1) * pas;

const supportAire = (L, l) => {
    const A0 = L * l;
    return {
        sousType: 'aire',
        lettre: 'x',
        xMaxDomaine: xMaxAire(l),
        valeur: (x) => A0 - 4 * x * x,
        // Second chemin : l'aire par morceaux, la bande du milieu puis les deux bandes entre les carrés.
        controle: (x) => (L - 2 * x) * l + 2 * x * (l - 2 * x),
        repere: {
            xMin: 0, yMin: 0, xMax: Math.floor(l / 2) + 1, yMax: multipleAuDessus(A0, 20),
            xStep: 1, yStep: 4,
            xLabel: 'côté du carré (en cm)', yLabel: 'aire blanche (en cm²)', grille: true,
        },
        situation: `<p>On enlève quatre carrés superposables aux quatre coins d’un rectangle de ${L} cm de longueur et ${l} cm de largeur.</p>`
            + `<p>On note ${tex('x')} le côté d’un carré, en cm.</p>`,
        formule: `<p>L’aire blanche, en cm², est ${tex(`A = ${A0} - 4x^2`)}.</p>`,
        figure: imgElement(svgAireRestante(L, l), 'small', ALT_AIRE_RESTANTE),
        question: 'l’aire blanche en fonction du côté du carré',
        params: { L, l },
        distracteurs: [
            { code: 'carre_oublie', f: (x) => A0 - 4 * x },
            { code: 'aire_enlevee', f: (x) => 4 * x * x },
            { code: 'echange_xy', miroir: true },
        ],
    };
};

// ═════════ Support « tarif » : la carte d'abonnement du cours ═════════

const F_TARIF = [12, 16, 24, 32];
const V_TARIF = 2;
const N_MAX_TARIF = 8;
const LIEUX_TARIF = ['la piscine', 'la patinoire', 'le parc aquatique', 'le club de tennis'];
const CONTEXTES_TARIF = CONTEXTES.filter((c) => LIEUX_TARIF.includes(c.lieu));

const supportTarif = (F, ctx) => ({
    sousType: 'tarif',
    lettre: 'n',
    xMaxDomaine: N_MAX_TARIF,
    valeur: (n) => F + V_TARIF * n,
    // Second chemin : l'histoire, l'abonnement puis 2 € à chaque entrée.
    controle: (n) => { let p = F; for (let k = 0; k < n; k++) p += V_TARIF; return p; },
    repere: {
        xMin: 0, yMin: 0, xMax: N_MAX_TARIF + 1, yMax: multipleAuDessus(F + V_TARIF * N_MAX_TARIF, 10),
        xStep: 1, yStep: V_TARIF,
        xLabel: `nombre ${deMot(ctx.actPl)}`, yLabel: 'prix (en €)', grille: true,
    },
    situation: `<p>${auLieu(ctx.lieu, true)}, ${ctx.abo} coûte ${F} €, puis chaque ${ctx.act} coûte ${V_TARIF} €.</p>`,
    formule: `<p>Le prix payé, en €, pour ${tex('n')} ${ctx.actPl} est ${tex(`P = ${F} + ${V_TARIF}n`)}.</p>`,
    figure: null,
    question: `le prix en fonction du nombre ${deMot(ctx.actPl)}`,
    params: { F, lieu: ctx.lieu },
    distracteurs: [
        { code: 'par_l_origine', f: (n) => V_TARIF * n },
        { code: 'coefficients_echanges', f: (n) => V_TARIF + F * n },
        { code: 'echange_xy', miroir: true },
    ],
});

/**
 * Un support à parts égales ; `troisNonNulles` impose trois abscisses non
 * nulles (les couples à l ≥ 7, tous les tarifs).
 */
const tirerSupport = ({ troisNonNulles = false } = {}) => {
    if (randInt(0, 1) === 0) {
        const eligibles = troisNonNulles ? COUPLES_AIRE.filter((c) => xMaxAire(c.l) >= 3) : COUPLES_AIRE;
        const { L, l } = randomChoice(eligibles);
        if (L === SACRIFIE_AIRE.L && l === SACRIFIE_AIRE.l) throw new Error('gen_176 : couple sacrifié');
        return supportAire(L, l);
    }
    const F = randomChoice(F_TARIF);
    if (F === SACRIFIE_TARIF.F && V_TARIF === SACRIFIE_TARIF.v) throw new Error('gen_176 : tarif sacrifié');
    return supportTarif(F, randomChoice(CONTEXTES_TARIF));
};

/** Les points (x ; f(x)) contrôlés par le second chemin, sur la grille et dans la fenêtre (throw sinon). */
const pointsControles = (s, xs) => xs.map((x) => {
    const y = s.valeur(x);
    if (s.controle(x) !== y) throw new Error(`gen_176 : ${s.sousType}, ${s.lettre} = ${x} : ${y} contre ${s.controle(x)} par le second chemin`);
    const r = s.repere;
    if (x < r.xMin || x > r.xMax || y < r.yMin || y > r.yMax) throw new Error(`gen_176 : point (${x} ; ${y}) hors de la fenêtre`);
    if (x % r.xStep !== 0 || y % r.yStep !== 0) throw new Error(`gen_176 : point (${x} ; ${y}) hors de la grille`);
    return { x, y };
});

// ═════════ Carte 1 — Placer les points du graphique (Repère) ═════════

// Ni A (l'aire) ni P (le prix) ni O (l'origine) ni I ; l'ordre alphabétique suit les abscisses.
const LETTRES_POINTS = 'BCDEGHJKMNRSTUVW'.split('');

const genPoints = () => {
    const avecZero = randInt(1, 3) === 1;                       // x = 0 dans un tirage sur trois au plus
    const s = tirerSupport({ troisNonNulles: !avecZero });
    const nonNulles = Array.from({ length: s.xMaxDomaine }, (_, i) => i + 1);
    const xs = (avecZero ? [0, ...randomChoiceMultiple(nonNulles, 2)] : randomChoiceMultiple(nonNulles, 3)).sort((a, b) => a - b);
    if (new Set(xs).size !== 3) throw new Error('gen_176 : abscisses confondues');
    const pts = pointsControles(s, xs);
    const noms = pickLettresSains(LETTRES_POINTS, 3).sort();
    const bonne = {};
    pts.forEach((p, i) => { bonne[noms[i]] = `${p.x};${p.y}`; });
    const demandes = pts.map((p, i) => `le point ${noms[i]} d’abscisse ${tex(`${s.lettre} = ${p.x}`)}`);
    const texte = `${s.situation}${s.formule}${RAPPEL_BROUILLON}`
        + `<p><b>Place ${demandes[0]}, ${demandes[1]} et ${demandes[2]}.</b></p>`;
    const payload = basePayload('association', texte, {
        subType: 'placer_points_2d',
        repere: s.repere,
        pointsToPlace: noms,
        bonne_reponse: JSON.stringify(bonne),
        // Le point fantôme n'affiche pas ses coordonnées : lire l'échelle EST le savoir évalué (§9bis).
        masquerCoordonnees: true,
        sousType: s.sousType,
    }, { ...opts(L_POINTS), elements_additionnels: s.figure });
    payload._meta.params = { sousType: s.sousType, ...s.params, xs, ys: pts.map((p) => p.y), points: noms };
    return payload;
};

// ═════════ Carte 2 — Quel graphique ? (QCM d'images) ═════════

const PANNEAU = { W: 320, H: 230, padL: 44, padR: 16, padT: 26, padB: 36 };
const ETIQUETTES_NICE = [1, 2, 4, 5, 8, 10, 20, 25, 40, 50, 100];

/** Le pas des étiquettes d'un axe : le plus petit pas « rond », multiple du pas de la grille, espacé d'au moins `mini` px. */
const pasEtiquettes = (max, step, pxParUnite, mini) =>
    ETIQUETTES_NICE.find((k) => k % step === 0 && k * pxParUnite >= mini) || max;

/**
 * La courbe y = f(x) de 0 à xFin, échantillonnée, coupée exactement au bord
 * du cadre (0 ou yMax) : la droite raide de coefficients_echanges sort par le haut.
 */
const tracerCourbe = (f, xFin, yMax) => {
    const N = 48;
    const out = [];
    let prev = null;
    for (let i = 0; i <= N; i++) {
        const x = (xFin * i) / N;
        const y = f(x);
        const dedans = y >= 0 && y <= yMax;
        if (prev && dedans !== prev.dedans) {
            const yb = (dedans ? prev.y : y) > yMax ? yMax : 0;
            const t = (yb - prev.y) / (y - prev.y);
            out.push({ x: prev.x + t * (x - prev.x), y: yb });
        }
        if (dedans) out.push({ x, y });
        prev = { x, y, dedans };
    }
    return out;
};

/** Un panneau : le repère (grille, axes fléchés, étiquettes, titres sans lettre), le trait fin, les croix. */
const svgPanneau = ({ xMax, xStep, yMax, yStep, xLabel, yLabel, points, trace }) => {
    const { W, H, padL, padR, padT, padB } = PANNEAU;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const X = (x) => padL + (x / xMax) * plotW;
    const Y = (y) => padT + plotH - (y / yMax) * plotH;
    const f1 = (v) => v.toFixed(1);
    const x0 = padL; const y0 = padT + plotH;
    let g = '';
    for (let x = xStep; x <= xMax + 1e-9; x += xStep) g += `<line x1="${f1(X(x))}" y1="${padT}" x2="${f1(X(x))}" y2="${y0}" stroke="#e2e8f0" stroke-width="1"/>`;
    for (let y = yStep; y <= yMax + 1e-9; y += yStep) g += `<line x1="${x0}" y1="${f1(Y(y))}" x2="${padL + plotW}" y2="${f1(Y(y))}" stroke="#e2e8f0" stroke-width="1"/>`;
    g += `<line x1="${x0}" y1="${y0}" x2="${padL + plotW + 8}" y2="${y0}" stroke="#1e293b" stroke-width="1.3"/>`
        + `<polygon points="${padL + plotW + 12},${y0} ${padL + plotW + 5},${y0 - 3.5} ${padL + plotW + 5},${y0 + 3.5}" fill="#1e293b"/>`
        + `<line x1="${x0}" y1="${y0}" x2="${x0}" y2="${padT - 8}" stroke="#1e293b" stroke-width="1.3"/>`
        + `<polygon points="${x0},${padT - 12} ${x0 - 3.5},${padT - 5} ${x0 + 3.5},${padT - 5}" fill="#1e293b"/>`;
    const mx = pasEtiquettes(xMax, xStep, plotW / xMax, 24);
    const my = pasEtiquettes(yMax, yStep, plotH / yMax, 16);
    for (let x = 0; x <= xMax + 1e-9; x += mx) {
        g += `<line x1="${f1(X(x))}" y1="${y0 - 3}" x2="${f1(X(x))}" y2="${y0 + 3}" stroke="#1e293b" stroke-width="1"/>`
            + `<text x="${f1(X(x))}" y="${y0 + 13}" text-anchor="middle" font-size="9" fill="#334155">${x}</text>`;
    }
    for (let y = my; y <= yMax + 1e-9; y += my) {
        g += `<line x1="${x0 - 3}" y1="${f1(Y(y))}" x2="${x0 + 3}" y2="${f1(Y(y))}" stroke="#1e293b" stroke-width="1"/>`
            + `<text x="${x0 - 6}" y="${f1(Y(y) + 3)}" text-anchor="end" font-size="9" fill="#334155">${y}</text>`;
    }
    g += `<text x="${W - padR}" y="${H - 8}" text-anchor="end" font-size="10" font-weight="600" fill="#1e293b">${xLabel}</text>`
        + `<text x="${x0 + 7}" y="${padT - 9}" text-anchor="start" font-size="10" font-weight="600" fill="#1e293b">${yLabel}</text>`;
    g += `<polyline points="${trace.map((p) => `${f1(X(p.x))},${f1(Y(p.y))}`).join(' ')}" fill="none" stroke="#1E3A8A" stroke-width="1.2" stroke-linejoin="round"/>`;
    points.forEach((p) => {
        const cx = X(p.x); const cy = Y(p.y);
        g += `<path d="M${f1(cx - 4)},${f1(cy - 4)} L${f1(cx + 4)},${f1(cy + 4)} M${f1(cx - 4)},${f1(cy + 4)} L${f1(cx + 4)},${f1(cy - 4)}" stroke="#DC2626" stroke-width="1.6"/>`;
    });
    const dataPoints = points.map((p) => `${p.x}:${p.y}`).join(';');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-points="${dataPoints}" data-axes="${xLabel}|${yLabel}">`
        + `<rect width="${W}" height="${H}" fill="#ffffff"/>${g}</svg>`;
};

/**
 * Un panneau du QCM : les points entiers de 0 à xMaxDomaine qui tiennent dans
 * la fenêtre, le trait fin ; en miroir (echange_xy), les axes et chaque point
 * (x ; y) → (y ; x).
 */
const construirePanneau = (s, { code, f = s.valeur, miroir = false }) => {
    const r = s.repere;
    const entiers = [];
    for (let x = 0; x <= s.xMaxDomaine; x++) {
        const y = f(x);
        if (y >= 0 && y <= r.yMax) entiers.push({ x, y });
    }
    const swap = (q) => (miroir ? { x: q.y, y: q.x } : q);
    const axes = miroir
        ? { xMax: r.yMax, xStep: r.yStep, yMax: r.xMax, yStep: r.xStep, xLabel: r.yLabel, yLabel: r.xLabel }
        : { xMax: r.xMax, xStep: r.xStep, yMax: r.yMax, yStep: r.yStep, xLabel: r.xLabel, yLabel: r.yLabel };
    const points = entiers.map(swap);
    return { code, points, svg: svgPanneau({ ...axes, points, trace: tracerCourbe(f, s.xMaxDomaine, r.yMax).map(swap) }) };
};

const genGraphique = () => {
    const s = tirerSupport();
    const panneaux = [{ code: null }, ...s.distracteurs].map((p) => construirePanneau(s, p));
    // Second chemin : la clé porte exactement les points (x ; f(x)) contrôlés par
    // l'autre calcul, x de 0 à xMaxDomaine ; quatre panneaux deux à deux distincts ;
    // un seul panneau dont tous les points sont sur la formule.
    const attendus = pointsControles(s, Array.from({ length: s.xMaxDomaine + 1 }, (_, i) => i));
    const signature = (pts) => pts.map((p) => `${p.x}:${p.y}`).join(';');
    if (signature(panneaux[0].points) !== signature(attendus)) throw new Error('gen_176 : la clé ne porte pas les points de la formule');
    if (new Set(panneaux.map((p) => signature(p.points))).size !== 4) throw new Error(`gen_176 : deux panneaux identiques (${s.sousType} ${JSON.stringify(s.params)})`);
    const surLaFormule = panneaux.filter((p) => p.points.length >= 3 && p.points.every((q) => s.valeur(q.x) === q.y));
    if (surLaFormule.length !== 1 || surLaFormule[0] !== panneaux[0]) throw new Error('gen_176 : un distracteur est sur la formule');
    const images = panneaux.map((p) => ({ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(p.svg)}` } }));
    const texte = `${s.situation}${s.formule}<p><b>Quel graphique représente ${s.question} ?</b></p>`;
    const payload = makeQcm(images, images[0], texte, { ...opts(L_GRAPHIQUE), elements_additionnels: s.figure });
    payload.contenu.sousType = s.sousType;
    payload._meta.params = {
        sousType: s.sousType, ...s.params, points: panneaux[0].points, distracteurs: panneaux.slice(1).map((p) => p.code),
    };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur NOMMÉE (codes dans _meta.params.distracteurs), jamais un panneau inventé au moment de l\'assemblage',
        'quatre panneaux SVG en imageData, MÊME repère que la carte Repère (fenêtre, pas, titres d\'axes sans lettre), croix sur les points entiers du domaine ET trait fin ; le panneau echange_xy a les titres d\'axes permutés et le dessin en miroir',
        'la clé porte exactement les points (x ; f(x)) recalculés par le second chemin ; quatre panneaux deux à deux distincts ; un seul panneau dont tous les points sont sur la formule (throw sinon)',
        'aucun trait propre à la seule clé : elle n\'est ni la seule décroissante, ni la seule courbe, ni la seule à couper l\'axe des ordonnées hors de l\'origine (mesuré dans gen176.self.test.js sur 2 000 tirages : 0 cas)',
        'chaque panneau porte data-points="x:y;…" (ses points tracés) et data-axes="titre x|titre y" : le solveur test-side retrouve la clé depuis la formule du texte, sans l\'aide du gen',
    ],
    graphique_qcm_unique: {
        support: 'sousType aire (rectangle L × l parmi dix couples, A = L × l − 4x², x de 0 à ⌊(l − 1)/2⌋, yStep 4) ou tarif (abonnement F ∈ {12 ; 16 ; 24 ; 32} puis 2 € l\'entrée, P = F + 2n, n de 0 à 8, yStep 2), à parts égales',
        aire: {
            cle: 'la courbe décroissante depuis (0 ; L × l)',
            familles: {
                carre_oublie: 'L × l − 4x : l\'aire d\'un carré prise pour son côté, une droite décroissante depuis (0 ; L × l) (famille de 175 ; DEPP 2de 2025 q.4 atteste le brouillage 2x / x²)',
                aire_enlevee: '4x² : l\'aire enlevée tracée à la place de l\'aire blanche, une courbe croissante par l\'origine (inférée, analogue de DEPP 4e 2023 q.3 et q.13, le complémentaire pris pour la figure)',
                echange_xy: 'la grandeur « en fonction de laquelle » mise en ordonnée : titres d\'axes permutés, points (A ; x), dessin miroir (DEPP 4e 2023 q.20 : 35,9 % inversent abscisse et ordonnée)',
            },
            traits_partages: 'décroissante : clé, carre_oublie, echange_xy ; courbe : clé, aire_enlevee, echange_xy ; coupe l\'axe des ordonnées hors de l\'origine : clé, carre_oublie',
        },
        tarif: {
            cle: 'la droite croissante depuis (0 ; F)',
            familles: {
                par_l_origine: '2n : la part fixe oubliée, la droite par l\'origine (le seul graphique du cours, Proport1 p. 2)',
                coefficients_echanges: '2 + F × n : les deux nombres échangés, la droite raide qui part de (0 ; 2) et sort par le haut du cadre (famille de 175)',
                echange_xy: 'titres d\'axes permutés, points (P ; n), dessin miroir',
            },
            traits_partages: 'croissante : les quatre ; droite : les quatre ; coupe l\'axe des ordonnées hors de l\'origine : clé, coefficients_echanges',
        },
        non_retenues: {
            parenthesage: '(F + 2) × n : par l\'origine et plus raide que la clé ; avec par_l_origine, deux distracteurs par l\'origine isoleraient la clé (cadrage, §6)',
            echelle_ignoree: 'un carreau lu comme une unité : une erreur de placement sur la carte Repère, pas un panneau (cadrage, §6)',
            gauche_a_droite_et_concatenation: 'erreurs de substitution (DEPP 4e 2023 q.17) : elles déplacent des points au placement, jamais un panneau',
        },
    },
};

// ═════════ Registre ═════════

const GENERATORS = {
    points_placer_points_2d: genPoints,
    graphique_qcm_unique: genGraphique,
};

export const CATEGORIES = [
    { id: 'points', label: L_POINTS, formatsList: [{ key: 'points_placer_points_2d', label: 'Repère' }] },
    { id: 'graphique', label: L_GRAPHIQUE, formatsList: [{ key: 'graphique_qcm_unique', label: 'QCM' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
